import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map } from 'rxjs';
import type {
  ContextSnapshot,
  QuotaRow,
} from '../models/flock-telemetry.models';
import type { WorkQueueResponse } from '../models/work-queue.model';
import { RynBridgeService } from '../services/ryn-bridge.service';
import type { HostDaemonReport } from './daemons.logic';
import type { HostSample } from './machines.logic';
import { buildHostReadings, buildHostSamples } from './host-readings.logic';
import type { PaceSampleMap } from './pace.logic';
import {
  PACE_HISTORY_HOURS,
  addCcPaceSamples,
  addRollupPaceSamples,
  paceHistoryDue,
  seedPaceHistory,
} from './pace-history.logic';
import type { QueueDepthRow, WallboardSeat } from './wallboard.logic';
import {
  PUSH_COALESCE_MS,
  RECONCILE_MS,
  WALLBOARD_PUSH_SOURCES,
  WallboardSource,
  bridgeErrorText,
  isNewerResponse,
  lastFullReadAt,
  shouldReloadPage,
} from './wallboard-live.logic';
import type { CcAttention, HostReading } from './attention-inputs.logic';
import type { WallboardPorts } from './wallboard-ports';

export type {
  WallboardCcPort,
  WallboardHostFeedPort,
  WallboardHubConnection,
  WallboardHubPort,
  WallboardPorts,
  WallboardReloadPort,
} from './wallboard-ports';
import type { ReviewTaskRow } from './needs-you.logic';
import { OpenPrIndex, withOpenPrs } from './open-prs.logic';
import { AccountAckSync } from './account-ack.sync';
import { InboundDismissSync } from './inbound-dismiss.sync';
import { OwedOccurrence, loadOwedOccurrences } from './owed-duties.logic';
import {
  DesiredRosterHost,
  RosterSeatRow,
  loadLatestHostMetrics,
  mergeDesiredRoster,
} from './desired-roster.logic';

/** The network sources: loaded on open, on push, and by the reconcile. */
export const WALLBOARD_NETWORK_SOURCES: readonly WallboardSource[] = [
  'quota',
  'context',
  'workQueue',
  'depths',
  'reviews',
  'owed',
  'roster',
];

/** Context snapshots older than this are not requested (WI 34293). */
const CONTEXT_STALE_MINUTES = 24 * 60;
/** Render clock: re-derives ages locally and runs the reconcile, reload and
 * polling checks. It never calls the network by itself. */
export const WALLBOARD_RENDER_CLOCK_MS = 30_000;
/** Review task statuses that can put an item in Needs you. */
const NEEDS_YOU_REVIEW_STATUSES = ['needs-human', 'green'];

/**
 * Data for the Roost wallboard (WI 34272), an unattended TV where every tile
 * must be a live reading (Tim, 2026-09-25), shared with Ryn's Roost mobile
 * (WI 34539). Platform pieces arrive through {@link createPorts}.
 *
 * - Host memory/disk: the hub's `hostMetrics` broadcast (host feed port).
 * - Host desired rosters: `GET /host-metrics/latest` (WI 34807), reconcile only.
 * - Quota, context, work-queue, depths and reviews: read on open, then
 *   re-read when the bridge pushes `quotaUpdated` / `contextSnapshot` /
 *   `openInboundChanged`. Pushes are change signals, not rows, so re-reading
 *   keeps the server-side transforms the single source of truth. Bursts
 *   coalesce into one re-read per source.
 * - A bounded reconcile re-reads everything every RECONCILE_MS, and on
 *   reconnect after a drop.
 * - Each source's last successful read feeds the STALE marker; a connection
 *   down past RELOAD_AFTER_DISCONNECT_MS triggers the platform's reload.
 */
// Field initializers run in declaration order and the public signals below
// read `this.ports`, so the private injected fields must come first; the
// public-before-private member-ordering rule would break initialization.
/* eslint-disable @typescript-eslint/member-ordering */ // pre-commit-allow: public signals read this.ports, so field init order must beat member-ordering (US 34539)
@Injectable()
export abstract class WallboardStore {
  private readonly bridge = inject(RynBridgeService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  /** Resolved once, during construction (an injection context). */
  private readonly ports: WallboardPorts = this.createPorts();

  readonly quotaRows = signal<QuotaRow[] | null>(null);
  readonly contextSnapshots = signal<ContextSnapshot[] | null>(null);
  readonly seats = signal<WallboardSeat[] | null>(null);
  /** Retired bridge rows, kept out of `seats` (WI 34807 tells auto from sticky). */
  readonly retiredSeats = signal<RosterSeatRow[] | null>(null);
  /** Each host's published desired roster, from the durable latest metrics (WI 34807). */
  readonly desiredRoster = signal<DesiredRosterHost[] | null>(null);
  readonly queueDepths = signal<QueueDepthRow[] | null>(null);
  readonly errors = signal<Partial<Record<WallboardSource, string>>>({});
  /** Last successful read per source, stamped with when that request was SENT
   * (the data is at least that fresh): the STALE marker's clock. */
  readonly lastUpdatedAt = signal<Partial<Record<WallboardSource, number>>>({});
  /** "Last full read": the oldest successful read across every source, so it
   * can never be newer than a tile's own clock. */
  readonly snapshotAt = computed(() =>
    lastFullReadAt(this.lastUpdatedAt(), WALLBOARD_NETWORK_SOURCES)
  );
  readonly paceSamples = signal<PaceSampleMap>(new Map());
  private readonly baseUrl = (): string => this.bridge.getBaseUrl();
  /** Shared account-alert acks (US 34815), read on every load. */
  readonly accountAcks = new AccountAckSync(this.http, this.baseUrl);
  /** Open-inbound Dismiss (Bug 35644): resolves a false WAITING row. */
  readonly inboundDismiss = new InboundDismissSync(this.http, this.baseUrl);
  /** Source errors plus a failed ack or dismiss, for the gaps lines. */
  readonly gapErrors = computed(() =>
    this.inboundDismiss.withError(this.accountAcks.withError(this.errors()))
  );
  readonly now = signal(Date.now());
  readonly hubConnected = this.ports.hub.connected;
  /** True while the hub is down on a platform that polls instead (mobile). */
  readonly pollingFallback = computed(
    () => this.ports.disconnectedPollMs !== null && !this.hubConnected()
  );
  /** Review tasks that can need Tim (needs-human, green). */
  readonly reviewTasks = signal<ReviewTaskRow[] | null>(null);
  /** Scheduled duties owed past 30 min (WI 35459), server-filtered. */
  readonly owedOccurrences = signal<OwedOccurrence[] | null>(null);
  /** Open PR ids per repo (Bug 34839); a failed repo reads as unknown. */
  readonly openPrs = signal<OpenPrIndex | null>(null);
  /** The Electron CC's local view; null in a plain browser or on mobile. */
  readonly ccAttention = signal<CcAttention | null>(null);
  /** True when this page runs inside a CC shell that serves cc:attention. */
  readonly ccAttentionSupported = this.ports.cc !== null;

  /** Newest hostMetrics per host plus the last minute's samples. */
  readonly hostReadings = computed<HostReading[]>(() =>
    buildHostReadings(this.ports.hostFeed.hosts(), (id) =>
      this.ports.hostFeed.getHistory(id)
    )
  );

  /** Each host's latest daemons report (WI 34278), live over hostMetrics. */
  readonly hostDaemons = computed<HostDaemonReport[]>(() =>
    this.ports.hostFeed.hosts().map((h) => ({
      hostId: h.hostId,
      hostname: h.hostname,
      daemons: h.daemons,
    }))
  );

  /** hostMetrics samples from the last minute per host, newest first. */
  readonly hostSamples = computed<HostSample[]>(() =>
    buildHostSamples(this.ports.hostFeed.hosts(), (id) =>
      this.ports.hostFeed.getHistory(id)
    )
  );

  private started = false;
  private destroyed = false;
  private everConnected = false;
  private droppedSinceLoad = false;
  private disconnectedSince: number | null = Date.now();
  private lastReconcileAt = 0;
  private paceHistoryReadAt: number | null = null;
  private readonly pending = new Set<WallboardSource>();
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly subscribedHubs = new WeakSet<object>();
  /** Per-source request generations: issued, last applied, and newest failed
   * (out-of-order guard; an older success must not clear a newer failure). */
  private readonly issuedGen = new Map<WallboardSource, number>();
  private readonly appliedGen = new Map<WallboardSource, number>();
  private readonly failedGen = new Map<WallboardSource, number>();
  private pushCleanup: (() => void) | null = null;

  constructor() {
    // Event-driven resync: a reconnect after a drop may have missed pushes,
    // so take one fresh snapshot.
    effect(() => {
      const connected = this.hubConnected();
      untracked(() => {
        this.disconnectedSince = connected
          ? null
          : (this.disconnectedSince ?? Date.now());
        if (connected) {
          if (this.everConnected && this.droppedSinceLoad) {
            this.droppedSinceLoad = false;
            this.load();
          }
          this.everConnected = true;
        } else if (this.everConnected) {
          this.droppedSinceLoad = true;
        }
      });
    });
  }

  /** The platform adapters. Called once while the store is constructed, so
   * implementations may use `inject()` but not their own fields. */
  protected abstract createPorts(): WallboardPorts;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.load();
    void this.ports.hostFeed.connect();
    void this.subscribePushes();
    const clock = setInterval(() => this.tick(), WALLBOARD_RENDER_CLOCK_MS);
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      clearInterval(clock);
      if (this.pendingTimer) clearTimeout(this.pendingTimer);
      // The hub connection is shared app-wide; leaving the dashboard must not
      // leave its handlers behind to stack on the next visit.
      this.pushCleanup?.();
      this.pushCleanup = null;
    });
  }

  /** Coalesces pushes: one re-read per source per PUSH_COALESCE_MS window. */
  requestReload(sources: readonly WallboardSource[]): void {
    if (this.destroyed) return;
    for (const s of sources) this.pending.add(s);
    if (this.pendingTimer) return;
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      const due = [...this.pending];
      this.pending.clear();
      for (const s of due) this.loadSource(s);
    }, PUSH_COALESCE_MS);
  }

  /** Manual refresh (the header button): one fresh snapshot of everything. */
  refresh(): void {
    this.load();
  }

  private async subscribePushes(): Promise<void> {
    try {
      const hub = await this.ports.hub.connect();
      // The user may leave while the shared hub negotiates: the destroy
      // callback already ran with nothing to clean up, so attach nothing.
      if (this.destroyed || this.subscribedHubs.has(hub)) return;
      this.subscribedHubs.add(hub);
      const handlers: [string, () => void][] = Object.entries(
        WALLBOARD_PUSH_SOURCES
      ).map(([target, sources]) => [target, () => this.requestReload(sources)]);
      for (const [target, handler] of handlers) hub.on(target, handler);
      this.pushCleanup = () => {
        for (const [target, handler] of handlers) hub.off(target, handler);
        this.subscribedHubs.delete(hub);
      };
    } catch {
      // No live connection: tiles show STALE and the tick's checks recover.
    }
  }

  /** 30 s: re-derive ages, re-read the CC view, run the bounded reconcile
   * (or the polling fallback), and reload a dead connection. */
  private tick(): void {
    const nowMs = Date.now();
    this.now.set(nowMs);
    const pollMs = this.ports.disconnectedPollMs;
    const due =
      nowMs - this.lastReconcileAt >= RECONCILE_MS ||
      (pollMs !== null &&
        !this.hubConnected() &&
        nowMs - this.lastReconcileAt >= pollMs);
    if (due) this.load();
    else void this.loadCcAttention();
    const reload = this.ports.reload;
    if (
      shouldReloadPage({
        disconnectedSinceMs: this.disconnectedSince,
        lastReloadAtMs: reload.readLastReload(),
        nowMs,
      })
    ) {
      reload.writeLastReload(nowMs);
      reload.reload();
      // Web navigates away here. Where "reload" only reconnects (mobile), the
      // new connection needs the push handlers and the host feed again.
      this.pushCleanup?.();
      this.pushCleanup = null;
      void this.ports.hostFeed.connect();
      void this.subscribePushes();
    }
  }

  private async loadCcAttention(): Promise<void> {
    const cc = this.ports.cc;
    if (!cc) return;
    try {
      const a = await cc.attention();
      this.ccAttention.set(a);
      this.recordCcPaceSamples(a);
      this.clearError('cc');
    } catch (err) {
      this.setError('cc', err);
    }
  }

  /** The CC's live statusline readings feed the recent-burn slope too. */
  private recordCcPaceSamples(a: CcAttention): void {
    this.paceSamples.set(addCcPaceSamples(this.paceSamples(), a, Date.now()));
  }

  private load(): void {
    if (this.destroyed) return;
    this.now.set(Date.now());
    this.lastReconcileAt = Date.now();
    for (const s of WALLBOARD_NETWORK_SOURCES) this.loadSource(s);
    void this.accountAcks.load();
    this.loadPaceHistory();
    void this.loadCcAttention();
  }

  private loadSource(source: WallboardSource): void {
    switch (source) {
      case 'quota':
        return this.loadQuota();
      case 'context':
        return this.loadContext();
      case 'workQueue':
        return this.loadWorkQueue();
      case 'depths':
        return this.loadDepths();
      case 'reviews':
        return this.loadReviewTasks();
      case 'owed':
        return this.read(
          'owed',
          loadOwedOccurrences(this.http, this.bridge.getBaseUrl()),
          (rows) => this.owedOccurrences.set(rows)
        );
      case 'roster':
        return this.read(
          'roster',
          loadLatestHostMetrics(this.http, this.baseUrl()),
          (r) => this.desiredRoster.update((p) => mergeDesiredRoster(p, r))
        );
      case 'cc':
        return void this.loadCcAttention();
    }
  }

  private loadQuota(): void {
    this.read('quota', this.bridge.getQuota(), (rows) => {
      this.quotaRows.set(rows);
      this.recordPaceSamples(rows);
    });
  }

  private loadContext(): void {
    this.read(
      'context',
      this.bridge.getContextSnapshots(CONTEXT_STALE_MINUTES),
      (rows) => this.contextSnapshots.set(rows)
    );
  }

  private loadWorkQueue(): void {
    // WI 34807 needs the retired rows; split off, every other tile is unchanged.
    this.read(
      'workQueue',
      this.http.get<WorkQueueResponse>(
        `${this.bridge.getBaseUrl()}/ryn-db/work-queue?showRetired=true`
      ),
      (resp) => {
        const rows = (resp?.seats ?? []) as WallboardSeat[];
        this.seats.set(rows.filter((r) => !r.retired));
        this.retiredSeats.set(rows.filter((r) => r.retired));
      }
    );
  }

  private loadDepths(): void {
    // Per-seat open-inbound depth, built for this dashboard (WI 34280).
    this.read(
      'depths',
      this.http.get<{ depths?: QueueDepthRow[] }>(
        `${this.bridge.getBaseUrl()}/ryn-db/open-inbound-depths`
      ),
      (resp) => this.queueDepths.set(resp?.depths ?? [])
    );
  }

  /**
   * One GET for a source. Overlapping reads are normal (page open vs a push,
   * a push vs the reconcile), so every request takes a generation and only a
   * response newer than the last one applied may touch the tile. Nothing is
   * cancelled: under a steady push stream a cancel-previous read could starve.
   */
  private read<T>(
    source: WallboardSource,
    request$: Observable<T>,
    apply: (value: T) => void
  ): void {
    const generation = (this.issuedGen.get(source) ?? 0) + 1;
    this.issuedGen.set(source, generation);
    const requestedAt = Date.now();
    request$.subscribe({
      next: (value) => {
        if (!isNewerResponse(generation, this.appliedGen.get(source) ?? 0))
          return;
        this.appliedGen.set(source, generation);
        apply(value);
        this.lastUpdatedAt.update((m) => ({ ...m, [source]: requestedAt }));
        // Only a read newer than the newest failure proves the source healthy
        // again; an older success landing late must leave the STALE marker up.
        if (isNewerResponse(generation, this.failedGen.get(source) ?? 0))
          this.clearError(source);
      },
      error: (err) => {
        // A failure older than the reading on screen says nothing about it.
        if (!isNewerResponse(generation, this.appliedGen.get(source) ?? 0))
          return;
        if (isNewerResponse(generation, this.failedGen.get(source) ?? 0))
          this.failedGen.set(source, generation);
        this.setError(source, err);
      },
    });
  }

  /** Review tasks that can need Tim, one read per status (the unfiltered
   * list is the whole history). */
  private loadReviewTasks(): void {
    const base = this.bridge.getBaseUrl();
    const tasks = forkJoin(
      NEEDS_YOU_REVIEW_STATUSES.map((status) =>
        this.http.get<{ tasks?: ReviewTaskRow[] }>(
          `${base}/ryn-db/review-tasks?status=${encodeURIComponent(status)}`
        )
      )
    ).pipe(map((resps) => resps.flatMap((r) => r?.tasks ?? [])));
    const withOpen = tasks.pipe(withOpenPrs(this.http, base));
    this.read('reviews', withOpen, ({ rows, open }) => {
      this.openPrs.set(open);
      this.reviewTasks.set(rows);
    });
  }

  /** One sample per distinct observation of each live account window, so a
   * reconnect snapshot can feed the recent-burn slope in projectPace. */
  private recordPaceSamples(rows: QuotaRow[]): void {
    this.paceSamples.set(
      addRollupPaceSamples(this.paceSamples(), rows, Date.now())
    );
  }

  /** US 34691: seed the 7d samples from the bridge's stored history, so a
   * fresh page has the 12h basis at once. Best-effort: on a failure the
   * projection keeps the since-open average and the next reconcile retries. */
  private loadPaceHistory(): void {
    const nowMs = Date.now();
    if (!paceHistoryDue(this.paceHistoryReadAt, nowMs)) return;
    this.paceHistoryReadAt = nowMs;
    this.bridge.getQuotaSamples(PACE_HISTORY_HOURS).subscribe({
      next: (rows) =>
        this.paceSamples.set(
          seedPaceHistory(this.paceSamples(), rows, Date.now())
        ),
      error: () => (this.paceHistoryReadAt = null),
    });
  }

  private setError(source: WallboardSource, err: unknown): void {
    const msg = bridgeErrorText(err);
    this.errors.update((m) => ({ ...m, [source]: msg }));
  }

  private clearError(source: WallboardSource): void {
    this.errors.update((m) => {
      const next = { ...m };
      delete next[source];
      return next;
    });
  }
}
/* eslint-enable @typescript-eslint/member-ordering */
